import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { isFulfilled, isRejected } from '@reduxjs/toolkit'
import { format } from 'date-fns'
import { generatePath } from 'react-router'
import { push } from 'connected-react-router'

import CustomPopup from '../../UI/Popup'
import CustomButton from '../../UI/Buttons/CustomButton'
import MultiselectInput from '../../UI/Inputs/MultiselectInput'
import GeneralForm from '../../Form'
import { AUTH_ROUTE } from '../../../pages/App/routes'

import { useDispatch, useSelector } from '../../../store'

import {
  toggleIsOpenAprrovalModalAction,
  setInitialStateAction
} from '../../../store/modal/approvalPost/slice'
import { postApprovalPostsThunk } from '../../../store/modal/approvalPost/thunks'
import { startNextApprovalRoundThunk, getPostApprovalInfoThunk } from '../../../store/managePost/thunks'
import { getProjectUsersByRoleThunk } from '../../../store/project/thunks'

import { TApprovalPosts } from '../../../store/modal/approvalPost/types'
import { TPostForApproval, TPostSocialManagePost } from '../../../store/managePost/newTypes'
import { ATTACHMENT_TYPE, TPostAttachment } from '../../../models/post'
import Roles from '../../../models/roles'
import { APPROVAL_TYPE, POST_SOCIAL_STATUS, POST_STATUS } from '../../../models/post'

import Example from '../../../assets/img/example/story.svg'

import './SendToApproveModal.scss'
import { useToasts } from 'react-toast-notifications'
import { TOAST_MESSAGE } from '../../UI/ToastNotification/ToastNotification'
import VideoPreview from '../../UI/VideoPreview'
import { openSchedulePostModal } from '../../../store/application/slice'
import { SCHEDULE_ACTION_TYPES } from '../../../store/application/types'
import { defineAvailablePostsActions } from '../../../models/postStatuses'
import { setPostToApproveAction } from '../../../store/managePost/slice'
import { OriginalSocialIcons } from '../../UI/Icons/socials'
import LoadableImage from '../../UI/LoadableImage'
import { SETTING_NAME } from '../../../store/project/types'
import { resetSkipApprovalAction } from '../../../store/modal/skipApprovalPost/slice'
import { skipApprovalPostsThunk, skipApprovalPostsSocialThunk } from '../../../store/modal/skipApprovalPost/thunks'



type TPostList = TPostForApproval & {
  postFirstAttachment: TPostAttachment | string
  postFirstMessage: string
}

interface ModalSendToApprovalProps {
  resetSelectedPosts?: () => any
}

const ModalSendToApproval: FC<ModalSendToApprovalProps> = ({ resetSelectedPosts }) => {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  // TODO change hardcode roles
  const {
    isApprovalModalOpen,
    postsForApproval,
    agencyRoles,
    loaders,
    skipLoaders,
    project: { id: projectId, projectUsers },
    skipApproval,
    postSocialsApprovalPlan,
    postSocials,
    selectedPostSocialId
  } = useSelector((state) => ({
    agencyRoles: state.agency.roles,
    project: state.project.currentProject,
    isApprovalModalOpen: state.approvalModal.is_approval_modal_open,
    postsForApproval: state.approvalModal.posts_for_approval,
    loaders: state.managePost.loaders,
    skipLoaders: state.skipApproval.inProgress,
    skipApproval: state.skipApproval.skipped_approvals,
    postSocialsApprovalPlan: state.managePost.approval.post_socials,
    postSocials: state.managePost.postSocials,
    selectedPostSocialId: state.managePost.selectedPostSocialId,
  }))

  const isUnapproved: boolean = useMemo(() => {
    const postSocial = postSocialsApprovalPlan.find(({ id }) => id === selectedPostSocialId)!
    if (postSocial) {
      return postSocial?.is_unapproved
    }
    return false
  }, [selectedPostSocialId, postSocialsApprovalPlan])

  const isExistRevisionsPostSocial: boolean = useMemo(() => {
    const postSocial = postSocials.find(({ id }) => id === selectedPostSocialId)!
    if (postSocial) {
      return postSocial?.status === POST_SOCIAL_STATUS.STATUS_REQUIRES_REVISION
    }
    return false
  }, [selectedPostSocialId, postSocials])

  const setUpForm = {
    initialValues: {
      members: []
    },
    validateOnBlur: false,
    onSubmit: async (values: any) => {
      const { members } = values
      // const defineApproversToPayload = (role: Roles) =>
      //   members.find((member: any) => member.member.role === role)
      // const internalApprover = defineApproversToPayload(Roles.INTERNAL_APPROVER)
      // const externalApprover = defineApproversToPayload(Roles.EXTERNAL_APPROVER)

      const postsIds: number[] = await Promise.all(postsForApproval.map(async ({ id }) => id))
      const payload: TApprovalPosts = {
        posts_ids: postsIds.filter((id) => !!id)
      }

      const defineExternalApproversToPayload = (role: Roles) =>
        members.filter((member: any) => member.member.role === role).map((member: any) => member.value)
      const defineInternalApproversToPayload = (role: Roles) =>
        members.filter((member: any) => member.member.role !== role).map((member: any) => member.value)
      const internalApprover = defineInternalApproversToPayload(Roles.EXTERNAL_APPROVER)
      const externalApprover = defineExternalApproversToPayload(Roles.EXTERNAL_APPROVER);

      if (internalApprover) payload.concrete_internal_approver_id = internalApprover
      if (externalApprover) payload.concrete_external_approver_id = externalApprover

      if (
        postsForApproval.length === 1 &&
        !defineAvailablePostsActions.sendToApprove(
          postsForApproval[0].status,
          postsForApproval[0].schedule
        ) && skipApproval.length === 0 && !isUnapproved && postsForApproval[0].status === POST_STATUS.STATUS_DRAFT
      ) {
        const { schedule, postSocials, ...post } = postsForApproval[0]
        dispatch(
          setPostToApproveAction({ latest_schedule: schedule, post_socials: postSocials, ...post })
        )
        dispatch(openSchedulePostModal(SCHEDULE_ACTION_TYPES.SCHEDULE_BEFORE_APPROVAL))
        closeInviteApprovalModal()
      } else {
        if(skipApproval.length > 0 && postsIds.length <=1){
          let responseSkipApproval;
          if(isExistRevisionsPostSocial){
            const masterContentSocial = postSocialsApprovalPlan.find(
              (socialPlan) => socialPlan.is_master_content
            )        
  
            const skipPayload = {
              post_social_id: masterContentSocial?masterContentSocial.id:selectedPostSocialId,
              skipped_approvals: skipApproval
           }
            responseSkipApproval = await dispatch(skipApprovalPostsSocialThunk(skipPayload))
  
          }else{
          const skipPayload = {
            post_id: postsIds[0],
            skipped_approvals: skipApproval
         }
         responseSkipApproval = await dispatch(skipApprovalPostsThunk(skipPayload))  
        }
         if (isFulfilled(responseSkipApproval)) {
          sendToApproval(payload)
         }else{
          addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
         }
        }else{
          sendToApproval(payload)
        }
      }
    }
  }

  const sendToApproval = async (payload:TApprovalPosts)=>{
    if(isExistRevisionsPostSocial){

      const masterContentSocialApproval = postSocialsApprovalPlan.find(
        (socialApproval) => socialApproval.is_master_content
      )

      const  responseStartApprovalFlow = await dispatch(startNextApprovalRoundThunk({ ...payload,post_social_id:masterContentSocialApproval?masterContentSocialApproval.id:selectedPostSocialId}))
      if (isFulfilled(responseStartApprovalFlow)) {
        await dispatch(getPostApprovalInfoThunk(payload.posts_ids[0]))
        addToast(TOAST_MESSAGE.POST_SENT_FOR_APPROVAL, { appearance: 'success' })
        closeInviteApprovalModal()
        
      } else if (isRejected(responseStartApprovalFlow)) {
        addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
      }
  
    }else{
    const responseStartApprovalFlow = await dispatch(postApprovalPostsThunk(payload))

      if (isFulfilled(responseStartApprovalFlow)) {
        addToast(TOAST_MESSAGE.POST_SENT_FOR_APPROVAL, { appearance: 'success' })
        closeInviteApprovalModal()
        dispatch(resetSkipApprovalAction())
        resetSelectedPosts && resetSelectedPosts()
      } else if (isRejected(responseStartApprovalFlow)) {
        addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
      }
    }
  }

  const postsForList = useMemo<TPostList[]>(() => {
    return postsForApproval.map((post) => {
      const { postSocials } = post

      const postFirstAttachment: TPostSocialManagePost | undefined = postSocials.find(
        ({ attachments }) => attachments.length
      )
      const firstMessage = postSocials.find((postSocial) => postSocial.message)
      const firstComment = postSocials.find((postSocial) => postSocial.first_comment)

      const postForList: TPostList = {
        ...post,
        postFirstMessage:
          firstMessage?.message || firstComment?.first_comment || 'Empty description',
        postFirstAttachment: postFirstAttachment?.attachments[0]
          ? postFirstAttachment?.attachments[0]
          : Example
      }

      return postForList
    })
  }, [postsForApproval])

  const transformRoleName = useCallback((role) => role.split('-').join(' '), [])

  const approversList = useMemo(
    () =>
      projectUsers
        .filter(
          ({ role ,setting}) =>
          (role === Roles.EXTERNAL_APPROVER ||
            role === Roles.INTERNAL_APPROVER ||
            role === Roles.AGENCY_ADMIN || 
            !!setting.find((val) => val.name === SETTING_NAME.CONTENT_MANAGER && val.value  == 'true'))   &&
            (!skipApproval.includes(APPROVAL_TYPE.TYPE_INTERNAL) || ( skipApproval.includes(APPROVAL_TYPE.TYPE_INTERNAL) && ![Roles.INTERNAL_APPROVER , Roles.CONTENT_CREATOR].includes(role))) 
            && (!skipApproval.includes(APPROVAL_TYPE.TYPE_EXTERNAL) || (skipApproval.includes(APPROVAL_TYPE.TYPE_EXTERNAL) && ![Roles.EXTERNAL_APPROVER ].includes(role)))
        )
        .map((user) => {
          return {
            member: {
              id: user.id,
              name: user.name,
              role: user.role,
              avatar: user.avatar_image_link,
              displayRole: !!user.setting.find((val) => val.name === SETTING_NAME.CONTENT_MANAGER && val.value  == 'true')?'content manager':transformRoleName(user.role)
            },
            value: user.id,
            label: user.name
          }
        }),
    [projectUsers, skipApproval]
  )

  const closeInviteApprovalModal = () => {
    dispatch(toggleIsOpenAprrovalModalAction())
    dispatch(setInitialStateAction())
    dispatch(resetSkipApprovalAction())
  }

  useEffect(() => {
    if(isApprovalModalOpen){
      dispatch(getProjectUsersByRoleThunk())
    }
  }, [isApprovalModalOpen])

  return (
    <CustomPopup
      open={isApprovalModalOpen}
      buttonClose={closeInviteApprovalModal}
      closeOnDocumentClick={false}
      className='send-to-approval'
    >
      <div className='send-to-approval__wrapper'>
        <div className='send-to-approval__wrapper__title'>Send to approval</div>
        <div className='send-to-approval__wrapper__list'>
          {postsForList.map(
            ({ postFirstAttachment, postFirstMessage, id, postSocials, schedule }) => (
              <div key={id} className='send-to-approval__wrapper__block data'>
                <div className='post-info'>
                  <div className='post-info__time'>
                    {format(new Date(`${schedule.date}T${schedule.time}`), 'h:mm a').toLowerCase()}
                  </div>
                  <div className='post-info__data'>
                    <div className='post-info__data__media-wrapper'>
                      {typeof postFirstAttachment === 'string' ? (
                        <LoadableImage media={postFirstAttachment} />
                      ) : postFirstAttachment.object_type === ATTACHMENT_TYPE.TYPE_VIDEO ? (
                        <VideoPreview
                          attachment={postFirstAttachment}
                          width={300}
                          className='post-info__data__media-wrapper__video'
                        />
                      ) : (
                        <LoadableImage media={postFirstAttachment} />
                      )}
                    </div>
                    <div className='post-info__data__name'>{postFirstMessage}</div>
                  </div>
                </div>
                <div className='socials'>
                  {postSocials.map(({ social_network: socialNetwork, id: postSocialId }) => (
                    <div key={`${socialNetwork}_${postSocialId}`} className='socials__item-wrapper'>
                      <div className={`socials__item-wrapper__item`}>
                        {OriginalSocialIcons[socialNetwork]}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
        <GeneralForm {...setUpForm}>
          <div className='send-to-approval__wrapper__approvers'>Choose people (optional)</div>
          {projectUsers.length  && projectUsers.length > 0 ?
            <div className='send-to-approval__wrapper__block approvers'>
              <MultiselectInput
                label='Agency role'
                name={`members`}
                placeholder='Choose members'
                options={approversList}
              />
            </div>:null
          }
          <div className='send-to-approval__wrapper__actions'>
            <CustomButton onClick={closeInviteApprovalModal} type='button' button='secondary'>
              Cancel
            </CustomButton>
            <CustomButton inProgress={loaders.post.approve || skipLoaders} type='submit' button='primary'>
              Send
            </CustomButton>
          </div>
        </GeneralForm>
      </div>
    </CustomPopup>
  )
}

export default ModalSendToApproval
